/** @param {unknown} value @param {boolean} [defaultValue=false] */
export function parseApiBoolean(value, defaultValue = false) {
  if (value === true || value === 1 || value === '1') return true
  if (value === false || value === 0 || value === '0') return false
  const key = String(value ?? '')
    .trim()
    .toLowerCase()
  if (['true', 'yes', 'y', 'on'].includes(key)) return true
  if (['false', 'no', 'n', 'off'].includes(key)) return false
  return defaultValue
}

/** Standard empty marker for missing / placeholder data in the UI. */
export const EMPTY_DISPLAY = '—'

/**
 * True when a value should not be shown as real data
 * (empty, dash placeholders, or API/UI "Unknown" labels).
 */
export function isUnknownDisplayValue(value) {
  const text = String(value ?? '').trim()
  if (!text) return true
  if (text === '—' || text === '--' || text === '-') return true
  return /^unknown(\b.*)?$/i.test(text)
}

/** Return a readable value, or EMPTY_DISPLAY when missing/Unknown. */
export function displayOrBlank(value, emptyLabel = EMPTY_DISPLAY) {
  const text = String(value ?? '').trim()
  if (!text || isUnknownDisplayValue(text)) return emptyLabel
  return text
}

export function formatShortDate(value) {
  if (value == null || value === '') return '—'
  const d = new Date(Number(value))
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function formatDateTime(value) {
  if (value == null || value === '') return '—'
  const raw = String(value).trim()
  let d
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const num = Number(raw)
    d = new Date(num < 1e12 ? num * 1000 : num)
  } else {
    d = new Date(value)
  }
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatLabel(value) {
  if (!value) return '—'
  return String(value).replace(/_/g, ' ')
}

export function formatNumber(value) {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  return Math.round(num).toLocaleString()
}

/** MMYYYY from API, e.g. "052026" → "May 2026" */
export function formatDashboardPeriod(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return '—'
  if (s.length !== 6) return s
  const month = Number(s.slice(0, 2))
  const year = Number(s.slice(2))
  if (!month || month < 1 || month > 12 || !year) return s
  const d = new Date(year, month - 1, 1)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** Current month as MMYYYY for dashboard API input */
export function currentDashboardPeriod() {
  const now = new Date()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  return `${mm}${now.getFullYear()}`
}

export function formatIsoDate(value) {
  if (value == null || value === '') return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Short relative time ("just now", "5m ago", "3h ago", "2d ago"), falling
 * back to formatShortDate once it's more than a week old so old rows don't
 * read as an oddly precise "23d ago".
 */
export function formatRelativeTime(value) {
  if (value == null || value === '') return '—'
  const d = new Date(Number(value))
  if (Number.isNaN(d.getTime())) return '—'

  const diffMs = Date.now() - d.getTime()
  const diffSec = Math.round(diffMs / 1000)

  if (diffSec < 0) return formatShortDate(value)
  if (diffSec < 45) return 'Just now'
  if (diffSec < 90) return '1m ago'

  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`

  const diffHour = Math.round(diffMin / 60)
  if (diffHour < 24) return `${diffHour}h ago`

  const diffDay = Math.round(diffHour / 24)
  if (diffDay < 7) return `${diffDay}d ago`

  return formatShortDate(value)
}

/** Time only with AM/PM (no date). */
export function formatTimeAmPm(raw) {
  if (raw == null || raw === '') return '—'
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function formatCallDuration(val) {
  if (val == null || val === '') return '—'
  const n = Number(val)
  if (!Number.isFinite(n)) return String(val)
  if (n < 60) {
    return `${Math.round(n)}s`
  }
  const m = Math.floor(n / 60)
  const s = Math.round(n % 60)
  return `${m}m ${s}s`
}

export function formatCost(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return Math.round(n).toLocaleString()
}

/** Credit amounts (credits). */
export function formatRupee(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return `${Math.round(n).toLocaleString()} 💵`
}

/** Organisation credit balance amount (not INR). */
export function formatCreditBalance(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return Math.round(n).toLocaleString()
}

/** Default country dial code when none is present (India). */
export const DEFAULT_PHONE_COUNTRY_CODE = '91'

function looksLikePhoneNumber(value) {
  const raw = String(value ?? '').trim()
  if (!raw || raw === '—') return false
  if (raw.includes('@')) return false
  const digits = raw.replace(/\D/g, '')
  return digits.length >= 7
}

/**
 * Display format for telephone numbers: +91-9449373014
 * Accepts 10-digit local, 91-prefixed, +91, or already formatted values.
 */
export function formatPhoneNumber(value, options = {}) {
  const {
    fallbackCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
    emptyLabel = '—',
  } = options

  if (value == null || value === '') return emptyLabel
  const raw = String(value).trim()
  if (!raw || raw === '—') return emptyLabel
  if (!looksLikePhoneNumber(raw)) return raw

  const canonicalMatch = raw.match(/^(\+\d{1,4})-(\d{6,15})$/)
  if (canonicalMatch) return raw

  const digits = raw.replace(/\D/g, '')
  if (!digits) return raw

  const countryCode =
    String(fallbackCountryCode).replace(/\D/g, '') || DEFAULT_PHONE_COUNTRY_CODE
  let national = digits

  if (digits.length > 10 && digits.startsWith(countryCode)) {
    national = digits.slice(countryCode.length)
  } else if (digits.length === 10) {
    national = digits
  } else if (digits.length === 11 && digits.startsWith('0')) {
    // Common data-entry artifact: a domestic trunk-prefix "0" left in front
    // of an otherwise-valid 10-digit mobile number (e.g. "09842715775").
    // Without this, the generic 1-3-digit fallback below misreads it as a
    // bogus country code -- "+098-42715775" instead of "+91-9842715775".
    national = digits.slice(1)
  } else if (countryCode === '91' && digits.length >= 12 && digits.startsWith('91')) {
    national = digits.slice(-10)
  } else if (digits.length > 10) {
    if (digits.startsWith('91') && digits.length >= 12) {
      return `+91-${digits.slice(-10)}`
    }
    const match = digits.match(/^(\d{1,3})(\d{7,14})$/)
    if (match) return `+${match[1]}-${match[2]}`
  }

  if (national.length < 7) return raw
  return `+${countryCode}-${national}`
}

export function formatPhoneNumbers(values, options = {}) {
  if (!Array.isArray(values)) return []
  return values
    .map((value) => formatPhoneNumber(value, { ...options, emptyLabel: '' }))
    .filter(Boolean)
}

/** Team member contact — e.g. +91-8344465482 when country code and mobile are present. */
export function formatUserContactDisplay(countryCode, mobileNumber) {
  const mobile = String(mobileNumber ?? '').replace(/\D/g, '').trim()
  if (!mobile) return null

  const rawCode = String(countryCode ?? '').trim()
  if (!rawCode) return formatPhoneNumber(mobile)

  const dialDigits = rawCode.replace(/\D/g, '')
  if (!dialDigits) return formatPhoneNumber(mobile)

  return formatPhoneNumber(`${dialDigits}${mobile}`)
}
